import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Divider,
} from '@mui/material';

/**
 * 엑셀 공정명 매칭 모호 시 후보 선택 다이얼로그
 * @param {{ open: boolean, groups: object[], onConfirm: (selections: Record<string, object>) => void, onCancel: () => void }} props
 */
function ExcelProcessAmbiguityDialog({
  open,
  groups = [],
  onConfirm,
  onCancel,
}) {
  const [applyToAll, setApplyToAll] = useState(false);
  const [selections, setSelections] = useState({});
  const [globalChoiceKey, setGlobalChoiceKey] = useState('');

  useEffect(() => {
    if (!open) return;
    const init = {};
    for (const g of groups) {
      if (g?.candidates?.length === 1) {
        init[g.processNm] = g.candidates[0];
      }
    }
    setSelections(init);
    setApplyToAll(false);
    setGlobalChoiceKey('');
  }, [open, groups]);

  const HandleGroupChange = (processNm, candidateKey) => {
    const group = groups.find((g) => g.processNm === processNm);
    const picked = group?.candidates?.find((c) => c.key === candidateKey);
    if (!picked) return;
    if (applyToAll) {
      const next = {};
      for (const g of groups) {
        const matched =
          g.candidates?.find((c) => c.key === candidateKey) ??
          g.candidates?.[0];
        if (matched) next[g.processNm] = matched;
      }
      setSelections(next);
      setGlobalChoiceKey(candidateKey);
    } else {
      setSelections((prev) => ({ ...prev, [processNm]: picked }));
    }
  };

  const HandleApplyToAllToggle = (checked) => {
    setApplyToAll(checked);
    if (checked && globalChoiceKey) {
      const next = {};
      for (const g of groups) {
        const matched =
          g.candidates?.find((c) => c.key === globalChoiceKey) ??
          g.candidates?.[0];
        if (matched) next[g.processNm] = matched;
      }
      setSelections(next);
    }
  };

  const allSelected =
    groups.length > 0 &&
    groups.every((g) => selections[g.processNm]?.key);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>공정 매칭 선택</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          동일한 공정명이 여러 건 있습니다. 엑셀 데이터를 반영할 공정을
          선택해 주세요.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={applyToAll}
              onChange={(e) => HandleApplyToAllToggle(e.target.checked)}
            />
          }
          label="모든 모호한 공정에 동일 선택 적용"
        />
        <Divider sx={{ my: 2 }} />
        {groups.map((g) => (
          <Box key={g.processNm} sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {g.processNm}
              {g.excelDepNm ? ` (엑셀 부서: ${g.excelDepNm})` : ''}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {g.rowCount ?? 0}행
              </Typography>
            </Typography>
            <RadioGroup
              value={selections[g.processNm]?.key ?? ''}
              onChange={(e) => HandleGroupChange(g.processNm, e.target.value)}
            >
              {(g.candidates ?? []).map((c) => (
                <FormControlLabel
                  key={c.key}
                  value={c.key}
                  control={<Radio size="small" />}
                  label={c.label}
                />
              ))}
            </RadioGroup>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>취소</Button>
        <Button
          variant="contained"
          disabled={!allSelected}
          onClick={() => onConfirm?.(selections)}
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExcelProcessAmbiguityDialog;
